/**
 * Design system tokens — TS constants + CSS variable references.
 *
 * Numeric constants mirror the byte-locked contract
 * (`src/contracts/native-design-system-v1.json`). CSS variable references
 * (`*Var`) point at the tokens defined in `src/app/artist-ds.css` so inline
 * styles resolve through the same cascade as the `.ds-*` classes.
 */

/** Color tokens as CSS variable references (resolve at use time). */
export const DSColor = {
	background: "var(--ds-background)",
	surface: "var(--ds-surface)",
	surfaceStrong: "var(--surface-strong)",
	textPrimary: "var(--ds-text-primary)",
	textSecondary: "var(--ds-text-secondary)",
	iconPrimary: "var(--ds-icon-primary)",
	iconSecondary: "var(--ds-icon-secondary)",
	divider: "var(--ds-divider)",
	accent: "var(--ds-accent)",
	destructive: "var(--ds-destructive)",
	onMediaPrimary: "var(--ds-on-media-primary)",
	glass: "var(--ds-glass)",
	glassSoft: "var(--ds-glass-soft)",
	glassStroke: "var(--ds-glass-stroke)",
} as const;

/** Spacing scale in pixels (contract: none..xxl). */
export const DSSpacing = {
	none: 0,
	xs: 4,
	sm: 8,
	md: 12,
	lg: 16,
	xl: 24,
	xxl: 32,
} as const;

/** Spacing tokens as CSS variable references. */
export const DSSpacingVar = {
	none: "0px",
	xs: "var(--ds-space-xs)",
	sm: "var(--ds-space-sm)",
	md: "var(--ds-space-md)",
	lg: "var(--ds-space-lg)",
	xl: "var(--ds-space-xl)",
	xxl: "var(--ds-space-xxl)",
} as const;

/** Radius scale in pixels (contract: none..full). */
export const DSRadius = {
	none: 0,
	sm: 4,
	md: 8,
	lg: 12,
	xl: 16,
	full: 9999,
} as const;

/** Radius tokens as CSS variable references. */
export const DSRadiusVar = {
	none: "0px",
	sm: "var(--ds-radius-sm)",
	md: "var(--ds-radius-md)",
	lg: "var(--ds-radius-lg)",
	xl: "var(--ds-radius-xl)",
	full: "var(--ds-radius-full)",
} as const;

/**
 * Concentric corner helper — inner radius = outer - inset, clamped to 0.
 * Mirrors `DSRadius.concentric(outer:inset:)` from the iOS DS.
 */
export function dsConcentricRadius(outer: number, inset: number): number {
	return Math.max(0, outer - inset);
}

/** Motion durations (ms) and ready-to-use CSS transition strings. */
export const DSMotion = {
	fast: 180,
	standard: 250,
	slow: 350,
	easeFast: "180ms cubic-bezier(0.4, 0, 0.2, 1)",
	easeStandard: "250ms cubic-bezier(0.22, 1, 0.36, 1)",
	springStandard: "350ms cubic-bezier(0.34, 1.56, 0.64, 1)",
} as const;

/** Layout metrics for compact/media/now-playing primitives (contract). */
export const DSMetrics = {
	compact: { contentWidth: 24, contentHeight: 32, symbolSize: 17 },
	mediaCircle: { diameter: 44, symbolSize: 20, countSize: 11, countSpacing: 6 },
	nowPlaying: { height: 10, symbolSize: 6, spacing: 4 },
} as const;

/** Button sizing contract (height / horizontal padding / symbol size). */
export const DSButtonMetrics = {
	height: { xs: 34, sm: 40, md: 44, lg: 50 },
	horizontalPadding: { xs: 6, sm: 16, md: 24, lg: 24 },
	symbolSize: { xs: 15, sm: 16, md: 16, lg: 16 },
} as const;

/** Glass rendering contract. */
export const DSGlass = {
	surfaceOpacity: 0.92,
	strokeWidth: 1,
	defaultRadius: DSRadius.xl,
	shapes: ["roundedRect", "capsule", "circle"] as const,
} as const;
